import { useCallback, useEffect, useRef } from 'react';

const AUTO_SCROLL_THRESHOLD_PX = 96;

const isNearBottom = (container: HTMLDivElement) =>
  container.scrollHeight - container.scrollTop - container.clientHeight <= AUTO_SCROLL_THRESHOLD_PX;

export const useChatAutoScroll = (trigger: unknown) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoScrollRef = useRef(true);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    shouldAutoScrollRef.current = isNearBottom(container);
  }, []);

  const enableAutoScroll = useCallback(() => {
    shouldAutoScrollRef.current = true;
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const container = containerRef.current;
    shouldAutoScrollRef.current = true;

    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
  }, []);

  useEffect(() => {
    if (!shouldAutoScrollRef.current) return;

    const frameId = window.requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container || !shouldAutoScrollRef.current) return;

      container.scrollTop = container.scrollHeight;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [trigger]);

  return {
    containerRef,
    handleScroll,
    enableAutoScroll,
    scrollToBottom,
  };
};
